import React from 'react';
import { useTranslation, useExperiments } from '@wix/yoshi-flow-editor';
import { SchedulingAgenda } from '../BookingSchedulingAgenda';
import { classes, st } from './Scheduling.st.css';
import SectionTitle from '../SectionTitle/SectionTitle';
import settingsParams from '../../settingsParams';
import { useSettings } from '@wix/tpa-settings/react';
import {
  SchedulingSectionStatus,
  SchedulingSectionViewModel,
} from '../../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import SectionText from '../SectionText/SectionText';
import TimezoneIndication from '../TimezoneIndication/TimezoneIndication';
import { SchedulingLocationViewModel } from '../../../../service-page-view-model/scheduling-location-view-model/schedulingLocationViewModel';
import { FloatingDropdown, TextButton } from 'wix-ui-tpa/cssVars';
import { SchedulingTimezoneViewModel } from '../../../../service-page-view-model/shceduling-timezone-view-model/schedulingTimezoneViewModel';
import { useRenderLocation } from '../useRenderLocation';
import { useServicePageActions } from '../useServicePageActions';
import { usePagination } from '../BookingSchedulingAgenda/usePagination';
import { SESSIONS_PAGINATION_EXPERIMENT } from '../../constants';

export type SchedulingProps = {
  className?: string;
  schedule?: SchedulingSectionViewModel;
  locationViewModel?: SchedulingLocationViewModel;
  timezoneViewModel?: SchedulingTimezoneViewModel;
  hasMoreEvents?: boolean;
  isFetchingMore?: boolean;
};

const CourseSummary = ({
  numberOfSessions,
  firstSessionDate,
  lastSessionDate,
  t,
}: {
  firstSessionDate?: string;
  lastSessionDate?: string;
  numberOfSessions?: number;
  t: any;
}) => {
  return (
    <div className={classes.courseSummary} data-hook="course-summary">
      <SectionText
        dataHook="scheduling-dates"
        text={t('app.scheduling.course-summary.dates', {
          firstSessionDate,
          lastSessionDate,
          interpolation: { escapeValue: false },
        })}
      />
      <SectionText
        dataHook="scheduling-number-of-sessions"
        text={t('app.scheduling.course-summary.total-number-of-sessions', {
          numberOfSessions,
        })}
      />
    </div>
  );
};

const SchedulingComponent = ({
  className,
  schedule,
  locationViewModel,
  timezoneViewModel,
  hasMoreEvents = false,
  isFetchingMore = false,
}: SchedulingProps) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { changeLocationCallback, changeTimezoneCallback, fetchMoreSessions } =
    useServicePageActions();
  const location = useRenderLocation();

  const showCourseSummary =
    schedule?.lastSessionDate && schedule?.firstSessionDate;
  const getNumberOfSessions = () =>
    schedule?.schedulingDaysViewModel?.reduce(
      (total, day) => total + day.dailySessions.length,
      0,
    );

  const totalDayGroups = schedule?.schedulingDaysViewModel?.length ?? 0;

  const isPaginationEnabled = experiments.enabled(
    SESSIONS_PAGINATION_EXPERIMENT,
  );
  const { currentPage, totalPages, pageSize, onPageChange } =
    usePagination(totalDayGroups);

  const hasLocationDropdown =
    locationViewModel?.locationOptions &&
    locationViewModel.locationOptions.length > 1;

  const onLocationChanged = (locationId: string) =>
    changeLocationCallback?.(locationId);

  const showPaginationControls =
    isPaginationEnabled &&
    schedule?.status === SchedulingSectionStatus.SUCCESS &&
    (totalPages > 1 || hasMoreEvents);

  const handleNextPage = () => {
    if (currentPage === totalPages - 1 && hasMoreEvents && fetchMoreSessions) {
      fetchMoreSessions();
      return;
    }
    onPageChange(currentPage + 1);
  };

  return (
    <div className={classes.root}>
      <SectionTitle
        title={
          settings.get(settingsParams.scheduleSectionTitle) ||
          settings.getDefaultValue(settingsParams.scheduleSectionTitle)
        }
        dataHook="scheduling-title"
        className={className}
      />
      {showCourseSummary && (
        <CourseSummary
          firstSessionDate={schedule!.firstSessionDate}
          lastSessionDate={schedule!.lastSessionDate}
          numberOfSessions={getNumberOfSessions()}
          t={t}
        />
      )}
      {(hasLocationDropdown || timezoneViewModel?.viewTimezone) && (
        <div className={classes.filtersRow}>
          {hasLocationDropdown ? (
            <FloatingDropdown
              className={st(classes.locationDropdown, { location })}
              data-hook="location-selection-dropdown"
              label=""
              options={locationViewModel!.locationOptions!.map((value) => ({
                ...value,
                isSelectable: true,
              }))}
              value={locationViewModel!.currentLocation}
              onChange={({ id: locationId }) => onLocationChanged(locationId!)}
            />
          ) : (
            <div />
          )}
          {timezoneViewModel?.viewTimezone ? (
            <TimezoneIndication
              changeTimezoneCallback={changeTimezoneCallback!}
              timezoneViewModel={timezoneViewModel}
            />
          ) : (
            <div />
          )}
        </div>
      )}
      <SchedulingAgenda
        viewModel={schedule}
        currentLocation={locationViewModel?.currentLocation}
        currentPage={currentPage}
        pageSize={pageSize}
        key={schedule?.status}
      />
      {showPaginationControls && (
        <div className={classes.pagination} data-hook="sessions-pagination">
          <TextButton
            data-hook="pagination-prev"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {t('app.scheduling.pagination.previous')}
          </TextButton>
          <span
            className={classes.pageIndicator}
            data-hook="pagination-indicator"
          >
            {currentPage + 1}
          </span>
          <TextButton
            data-hook="pagination-next"
            onClick={handleNextPage}
            disabled={
              isFetchingMore ||
              (currentPage === totalPages - 1 && !hasMoreEvents)
            }
          >
            {isFetchingMore
              ? t('app.scheduling.pagination.loading')
              : t('app.scheduling.pagination.next')}
          </TextButton>
        </div>
      )}
    </div>
  );
};

export default SchedulingComponent;
